<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
  } from 'vue';
  import { useOnline } from '@vueuse/core';
  import { useRouter } from 'vue-router/composables';
  import { StoreSetupService } from '@/api/services/store-setup.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { flowBar } from '@/composition/store-setup/setup-flow-screen';
  import type { IFlowBarState } from '@/composition/store-setup/setup-flow-screen';
  import { useStoreLiveLink } from '@/composition/store-setup/use-store-live-link';
  import { useGtag } from '@/composition/use-gtag';
  import { useUi } from '@/composition/use-ui';
  import { usesMenuQrVoseo } from '@/domain/menu-qr/menu-qr-copy';
  import { getServerErrorCode } from '@/domain/reservations/reservation-errors';
  import {
    hasActiveTrial,
    liveHeroKeys,
    liveShareTextKey,
    liveVariant,
  } from '@/domain/store-setup/live-screen';
  import { setupErrorKey } from '@/domain/store-setup/setup-errors';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IRestaurant } from '@/interfaces/IRestaurant';
  import type { IStoreSetupStatus } from '@/interfaces/IStoreSetup';
  import { ignoreNavigationFailure } from '@/utils/navigation.utils';

  /**
   * «¡Su tienda está en línea!» (plan §4.9): the store's link (copy, share on
   * WhatsApp, view as a customer), its QR (download the poster, print for
   * the tables) and the way to the dashboard. The variant (orders, menu only,
   * no menu yet) comes from the setup state. Opening it marks the Live screen
   * as seen; «Ir a mi panel» finishes the setup. Exposes the screen handle of
   * the page's bottom bar.
   */
  const QR_SIZE = 200;

  const props = defineProps<{
    restaurant: IRestaurant;
    status: IStoreSetupStatus;
  }>();
  const emit = defineEmits<{
    (e: 'dashboard'): void;
    (e: 'load-menu'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const router = useRouter();
  const online = useOnline();
  const ui = useUi();
  const gtag = useGtag();
  const link = useStoreLiveLink(() => props.restaurant);
  const { label, qrDataUrl, downloadingQr, copied, url } = link;

  /** `PATCH { stage: 'done' }` before going to the dashboard. */
  const leaving = ref(false);
  let alive = true;

  const variant = computed(() => liveVariant(props.status));
  const noMenu = computed(() => variant.value === 'noMenu');
  const hero = computed(() => liveHeroKeys(variant.value));
  const showTrial = computed(() =>
    hasActiveTrial(props.restaurant, new Date())
  );

  //#region Link and QR
  async function copyLink() {
    if (await link.copy()) gtag.event('live_share', { channel: 'copy' });
  }

  function shareWhatsapp() {
    const voseo = usesMenuQrVoseo(proxy.$i18n.locale, props.restaurant.country);
    const text = tr(liveShareTextKey(variant.value, voseo), { url: url.value });
    gtag.event('live_share', { channel: 'whatsapp' });
    void link.shareWhatsapp(text);
  }

  function viewAsDiner() {
    gtag.event('live_view_as_diner', {});
    void link.openAsDiner();
  }

  async function downloadQr() {
    if (await link.downloadQrPoster())
      gtag.event('live_qr', { action: 'download' });
  }

  function printQr() {
    gtag.event('live_qr', { action: 'print' });
    void router
      .push(`/${props.restaurant.stringId}/qr`)
      .catch(ignoreNavigationFailure);
  }
  //#endregion

  //#region Bottom bar
  /** «Ir a mi panel»: the setup is done, then the dashboard. */
  async function goDashboard() {
    if (leaving.value || !online.value) return;
    leaving.value = true;
    try {
      await StoreSetupService.patch(props.restaurant.id, { stage: 'done' });
    } catch (error) {
      console.error(error);
      ui.showToast(
        AlertType.Error,
        tr(
          setupErrorKey(getServerErrorCode(error)) ??
            'storeSetup.errors.generic'
        )
      );
      if (alive) leaving.value = false;
      return;
    }
    if (!alive) return;
    leaving.value = false;
    emit('dashboard');
  }

  const bar = computed<IFlowBarState>(() =>
    noMenu.value
      ? flowBar({
          helper: tr('storeSetup.live.helper'),
          // Offline it could not finish the setup.
          secondaryLabel: online.value
            ? tr('storeSetup.live.goDashboard')
            : null,
          primaryLabel: tr('storeSetup.live.loadMenu'),
          primaryDisabled: leaving.value,
        })
      : flowBar({
          helper: tr('storeSetup.live.helper'),
          primaryLabel: tr('storeSetup.live.goDashboard'),
          primaryDisabled: !online.value,
          primaryBusy: leaving.value,
        })
  );

  async function primary() {
    if (!noMenu.value) {
      await goDashboard();
      return;
    }
    if (!leaving.value) emit('load-menu');
  }

  async function secondary() {
    if (noMenu.value) await goDashboard();
  }

  /** The Live screen has no «Atrás». */
  function nothing(): Promise<void> {
    return Promise.resolve();
  }
  //#endregion

  onMounted(() => {
    // Marks the Live screen as seen (once, server side); nothing to tell the owner if it fails.
    StoreSetupService.patch(props.restaurant.id, {
      stage: 'live',
    }).catch((error) =>
      console.warn('[store-setup] live not marked as seen', error)
    );
  });

  onBeforeUnmount(() => {
    alive = false;
  });

  defineExpose({ bar, busy: leaving, primary, secondary, back: nothing });
</script>

<template>
  <div class="v2-store-live-screen">
    <header class="live-hero">
      <span class="hero-check" aria-hidden="true">
        <PosUiIcon name="check" :size="30" />
      </span>
      <h1 class="hero-title" tabindex="-1">{{ $t(hero.titleKey) }}</h1>
      <p class="hero-subtitle">{{ $t(hero.subtitleKey) }}</p>
    </header>
    <div class="live-grid">
      <section class="live-card link-card">
        <h2 class="card-label">{{ $t('storeSetup.live.link') }}</h2>
        <div class="link-box">
          <span class="link-text">{{ label }}</span>
          <PosUiButton
            class="link-copy"
            color="neutral"
            variant="outline"
            size="sm"
            :icon="copied ? 'check' : 'content-copy'"
            @click="copyLink"
            >{{
              copied ? $t('storeSetup.live.copied') : $t('storeSetup.live.copy')
            }}</PosUiButton
          >
        </div>
        <p v-if="noMenu" class="card-note">
          {{ $t('storeSetup.live.noMenuNote') }}
        </p>
        <div class="link-actions">
          <PosUiButton
            color="success"
            variant="soft"
            icon="whatsapp"
            @click="shareWhatsapp"
            >{{ $t('storeSetup.live.share') }}</PosUiButton
          >
          <PosUiButton
            color="neutral"
            variant="outline"
            icon="eye-outline"
            @click="viewAsDiner"
            >{{ $t('storeSetup.live.viewAsDiner') }}</PosUiButton
          >
        </div>
        <p v-if="showTrial" class="card-note">
          {{ $t('storeSetup.live.trial') }}
        </p>
      </section>
      <section class="live-card qr-card">
        <h2 class="card-label">{{ $t('storeSetup.live.qr') }}</h2>
        <div class="qr-frame">
          <img
            v-if="qrDataUrl"
            class="qr-image"
            :src="qrDataUrl"
            :alt="$t('storeSetup.live.qrAlt')"
            :width="QR_SIZE"
            :height="QR_SIZE"
          />
          <PosUiSkeleton v-else class="qr-image" />
        </div>
        <PosUiButton
          class="qr-download"
          color="neutral"
          variant="outline"
          size="sm"
          icon="download"
          :loading="downloadingQr"
          @click="downloadQr"
          >{{ $t('storeSetup.live.qrDownload') }}</PosUiButton
        >
        <button type="button" class="qr-print" @click="printQr">
          {{ $t('storeSetup.live.qrPrint') }}
        </button>
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-store-live-screen {
    @apply tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-5 md:tw-pt-4;
    .live-hero {
      @apply tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-text-center;
    }
    .hero-check {
      @apply tw-mb-1 tw-flex tw-h-[60px] tw-w-[60px] tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-50 tw-text-emerald-600;
      animation: live-check-in 300ms ease-out;
    }
    .hero-title {
      @apply tw-m-0 tw-text-xl tw-font-bold tw-text-neutral-900 md:tw-text-2xl;
    }
    .hero-subtitle {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500 md:tw-text-base;
    }
    // Link beside the QR where there is room; stacked on phones.
    .live-grid {
      @apply tw-grid tw-grid-cols-1 tw-items-start tw-gap-3;
      @media (min-width: 768px) {
        grid-template-columns: minmax(0, 1fr) 15rem;
      }
    }
    .live-card {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4;
    }
    .card-label {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .card-note {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .link-box {
      @apply tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-py-1.5 tw-pl-3 tw-pr-1.5;
    }
    // The whole link, always: a long slug wraps instead of being cut on phones.
    .link-text {
      @apply tw-min-w-0 tw-flex-1 tw-break-all tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    // «sm» buttons in a touch flow still get a 44 px hit area.
    .link-copy,
    .qr-download {
      @apply tw-min-h-11 tw-shrink-0;
    }
    .link-actions {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .qr-card {
      @apply tw-items-center tw-text-center;
    }
    .qr-frame {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-2.5;
    }
    .qr-image {
      @apply tw-block tw-h-[200px] tw-w-[200px] tw-max-w-full;
    }
    .qr-print {
      @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4 hover:tw-text-neutral-700;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }

  @keyframes live-check-in {
    from {
      transform: scale(0.6);
      opacity: 0;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-store-live-screen .hero-check {
      animation: none;
    }
  }
</style>
